import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, Subject, throwError, switchMap } from 'rxjs';
import { ErrorBlock, ProblemDetails, StructuredValidationProblemDetails } from '../models/problem-details';
import { MessageService } from '../services/message.service';
import { AuthenticationService } from '../services/authentication.service';
import { Router } from '@angular/router';
import UrlHelper from '../helpers/url-helper';
import { CustomProblemDetailTypes } from '../services/api-services';
import { StringHelper } from '../helpers/string.helper';

@Injectable({
    providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private messageService: MessageService,
        private router: Router,
        private authenticationService: AuthenticationService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: any) => {
                if (error.error instanceof ErrorEvent) {
                    this.messageService.openUnexpectedError();
                    return throwError(() => error.error);
                }
                else {
                    switch (error.status) {
                        case 401:
                            // Do not display an error message, since authentication interceptor will retry
                            break;
                        case 403:
                            if (error.error?.size) {
                                return this.parseErrorBlob(error).pipe(
                                    switchMap(parsedError => {
                                        if (typeof parsedError !== 'string' && parsedError.detail === 'AccountDeactivated' && !request.url.includes('/Authentication/Logout')) {
                                            this.authenticationService.logout().subscribe(() => {
                                                this.router.navigate(['/authentication/login'], { state: { accountDeactivated: true } });
                                            });
                                        }
                                        return throwError(() => error);
                                    })
                                );
                            } else if (error.error?.detail === 'AccountDeactivated' && !request.url.includes('/Authentication/Logout')) {
                                this.authenticationService.logout().subscribe(() => {
                                    this.router.navigate(['/authentication/login'], { state: { accountDeactivated: true } });
                                });
                            }
                            // Do not display because user will be redirected to login
                            break;
                        case 504:
                            // Do not display, gateway timeout has no impact for the user
                            break;
                        case 503:
                            this.messageService.openError('Service unavailable. Please try again later.');
                            break;
                        case 400:
                        case 404:
                        case 405:
                        case 409:
                        case 429:
                        case 500:
                            if (error.error?.detail || error.error?.title) {
                                const problemDetails = error.error as ProblemDetails;

                                this.showProblemDetailsErrorMessage(problemDetails);
                            } else if (error.error?.size) {
                                return this.parseErrorBlob(error).pipe(
                                    switchMap(parsedError => {
                                        if (typeof parsedError === 'string') {
                                            this.messageService.openUnexpectedError();
                                            return throwError(() => parsedError);
                                        }

                                        if (parsedError.detail === CustomProblemDetailTypes[CustomProblemDetailTypes.CaseDataNotFound]) {
                                            return this.handleCaseDataNotFound(error);
                                        }
                                        else if (parsedError.detail === CustomProblemDetailTypes[CustomProblemDetailTypes.PersonDataNotFound]) {
                                            return this.handlePersonDataNotFound(error);
                                        }

                                        this.showProblemDetailsErrorMessage(parsedError);
                                        return throwError(() => parsedError);
                                    })
                                );
                            }
                            else if (error.error?.errors) {
                                this.messageService.openError(error.error.errors.join('\n'));
                            } else {
                                this.messageService.openUnexpectedError();
                            }
                            break;
                        default:
                            this.messageService.openUnexpectedError();
                            break;
                    }

                    return throwError(() => error);
                }
            })
        );
    }

    private handleCaseDataNotFound(error: any) {
        // Silently redirect to cases page
        this.router.navigate([UrlHelper.casesPage]);
        return throwError(() => error);
    }

    private handlePersonDataNotFound(error: any) {
        // Silently redirect to persons page
        this.router.navigate([UrlHelper.peoplePage]);
        return throwError(() => error);
    }

    private showProblemDetailsErrorMessage(problemDetails?: ProblemDetails): void {
        if (problemDetails) {
            const structured = problemDetails as StructuredValidationProblemDetails;
            if (structured.errorBlocks?.length) {
                const html = structured.errorBlocks
                    .map(block => this.buildErrorBlockHtml(block))
                    .join('<hr class="my-2">');
                this.messageService.openError(html);
                return;
            }

            if ((problemDetails as any).errors && Object.keys((problemDetails as any).errors).length) {
                this.messageService.openError(Object.values((problemDetails as any).errors).join('\n'));
            }
            else if (problemDetails.detail) {
                this.messageService.openError(problemDetails.detail);
            }
            else if (problemDetails.title) {
                this.messageService.openError(problemDetails.title);
            }
            else {
                this.messageService.openUnexpectedError();
            }
        }
        else {
            this.messageService.openUnexpectedError();
        }
    }

    private parseErrorBlob(errorResponse: HttpErrorResponse): Observable<ProblemDetails | string> {
        var subject = new Subject<ProblemDetails | string>();

        let reader = new FileReader();
        reader.onload = () => {
            let result = reader?.result as string;

            try {
                result = JSON.parse(result);
            }
            catch {
                // Response error is not a JSON object and might be a server error (HTML etc.)
            }

            subject.next(result);
        };
        reader.readAsText(errorResponse.error);

        return subject.asObservable();
    }

    /**
     * Builds the HTML markup for a single error block.
     *
     * Structure (each part is conditional on non-empty value):
     *   <strong>  title       </strong>  — bold heading; omitted when title is blank
     *   <p>       description </p>       — body text; omitted when description is blank
     *   <ul><li>  items       </li></ul> — invoice list; omitted when items array is empty
     *   <p>       footer      </p>       — trailing note; omitted when footer is absent or blank
     *
     * All user-supplied text is HTML-encoded via StringHelper.htmlEncode to prevent XSS.
     */
    private buildErrorBlockHtml(block: ErrorBlock): string {
        const title = block.title?.trim()
            ? `<strong class="fs-5 d-block">${StringHelper.htmlEncode(block.title)}</strong>`
            : '';

        const description = block.description?.trim()
            ? `<p class="mb-1">${StringHelper.htmlEncode(block.description)}</p>`
            : '';

        // Items list is only rendered when there is at least one entry.
        const itemList = block.items?.length
            ? `<ul class="mb-0">${block.items.map(item => `<li>${StringHelper.htmlEncode(item)}</li>`).join('')}</ul>`
            : '';

        // Footer is an optional trailing note.
        const footer = block.footer?.trim()
            ? `<p class="mt-1 mb-0">${StringHelper.htmlEncode(block.footer)}</p>`
            : '';

        return title + description + itemList + footer;
    }
}